import React, { useEffect } from "react";
import moment from "moment";
import store from "@src/store";
import { AppSlice } from "@src/store/reducers/AppSlice";
import { AxiosRequestConfig } from "axios";
import { BaseAxiosApi } from "@src/hooks/useAxiosInstance";
import { PageContextIntl } from "@src/components/general/WrapPageElement/PageContext";

export interface IIsoLangContext {
  isoLang?: string;
  setIsoLang: (iso_lang: string) => void;
}

// Imports littéraux (pas de import() par variable, cassait le chargement en
// runtime) — chaque case est exclue du bundle par locale via DefinePlugin
// (`__MOMENT_LOCALE_*__`, onCreateWebpackConfig.ts) quand hors MOMENT_LOCALES.
const importMomentLocale = async (localeCode: string): Promise<void> => {
  switch (localeCode) {
    case "fr":
      if (__MOMENT_LOCALE_FR__) await import("moment/locale/fr");
      return;
    case "nl-be":
      if (__MOMENT_LOCALE_NL_BE__) await import("moment/locale/nl-be");
      return;
    case "de":
      if (__MOMENT_LOCALE_DE__) await import("moment/locale/de");
      return;
    case "es":
      if (__MOMENT_LOCALE_ES__) await import("moment/locale/es");
      return;
    case "en-gb":
      if (__MOMENT_LOCALE_EN_GB__) await import("moment/locale/en-gb");
      return;
    default:
      await import(
        /* webpackInclude: /moment[\\/]locale[\\/].+\.js$/ */
        `moment/locale/${localeCode}`
      );
  }
};

// Utility function to update global moment.js locale based on language code
export const updateMomentLocale = async (language: string): Promise<boolean> => {
  const momentJsLocale = language || "en";

  let localeCode = momentJsLocale;
  if (momentJsLocale === "nl") {
    localeCode = "nl-be";
  }

  if (localeCode === "en" || localeCode === "") {
    moment.locale("en");
    return true;
  }

  try {
    await importMomentLocale(localeCode);
    moment.locale(localeCode);
    return true;
  } catch (error) {
    console.warn(`Failed to load moment locale: ${localeCode}`, error);
    moment.locale("en");
    return false;
  }
};

const IsoLangContext = React.createContext<IIsoLangContext>({
  setIsoLang: (iso_lang) => {
    console.warn("noop", iso_lang);
  },
});
export default IsoLangContext;

export type IsoLangWrapPageElementProps = React.PropsWithChildren<{
  pageContext: PageContextIntl;
}>;

const IsoLangWrapPageElement = ({
  pageContext,
  children,
}: IsoLangWrapPageElementProps) => {
  const { language } = pageContext.intl;
  useEffect(() => {
    if (language) {
      // Update Redux store
      store.dispatch(AppSlice.actions.setIsoLang(language));
      // Update moment.js locale to match the page language
      updateMomentLocale(language);
    }
    const axiosInterceptor = UseIsoLangAxiosInterceptor(language);
    return () => {
      EjectIsoLangAxiosInterceptor(axiosInterceptor);
    };
  }, [language]);
  return <>{children}</>;
};
const UseIsoLangAxiosInterceptor = (isoLang: string) => {
  return BaseAxiosApi.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const { url } = config;
      let prefixedUrl = url && isoLang && "/" + isoLang + url;
      return { ...config, ...{ url: prefixedUrl } };
    }
  );
};

const EjectIsoLangAxiosInterceptor = (interceptorId: number) => {
  console.log("EjectIsoLangAxiosInterceptor", interceptorId);
  BaseAxiosApi.interceptors.request.eject(interceptorId);
};

export {
  IsoLangWrapPageElement,
  UseIsoLangAxiosInterceptor,
  EjectIsoLangAxiosInterceptor,
};
